import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { APPROVED_PR_STATUS_FILTER } from "Constants/ApprovedPRConstant";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";

function mergeFiltersWithStatusFilter(userFilters: any = []) {
  const arr = Array.isArray(userFilters) ? userFilters : [];
  
  const hasStatusFilter = arr.some(
    (f:any) => f?.filterField === "status"
  )

  if(hasStatusFilter){
    return arr
  }

  // Remove any user-selected status - we always enforce the default for Approved PR list
  const withoutStatus = arr.filter((f: any) => f?.filterField !== "status");
  // Default status filter must always be first so it's never overwritten
  return [APPROVED_PR_STATUS_FILTER, ...withoutStatus];
}

export const fetchApprovedPurchaseRequest = createAsyncThunk(
  "approvedpr/fetchApprovedPR",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    type,
    search_type,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    type?: string;
    search_type?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any[];
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      if (search) params.append("search", search);
      if (type === "tag" || search_type === "tag") {
        params.append("type", "tag");
        params.append("search_type", "tag");
      }
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      const mergedFilters = mergeFiltersWithStatusFilter(filters);
      const filterPayload = FilterBuilder(mergedFilters);

      const body = {
        filters: filterPayload,
        ...(type === "tag" || search_type === "tag"
          ? { type: "tag", search_type: "tag" }
          : {}),
      };

      const response: any = await Axios.post(
        `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_REQUEST?.LIST}?${params.toString()}`,
        body
      );
      return {
        data: response.data.data || [],
        rowCount: response.data.count || 0,
      };
    } catch (error: any) {
      throw new Error("Failed to fetch Approved Purchase Request List");
    }
  }
);

export const exportApprovePurchaseRequestCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: {
    filterField: string;
    filterValue: any;
  }[];
}) => {
  const params = new URLSearchParams();

  if (search) params.append("search", search);

  const mergedFilters = mergeFiltersWithStatusFilter(filters);
  const filterPayload = FilterBuilder(mergedFilters);

  const body = {
    filters: filterPayload,
  };

  const response = await Axios.post(
    `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_REQUEST?.EXPORT}?${params.toString()}`,
    body,
    { responseType: "blob" }
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") ||
      `purchase_request_${new Date().toISOString().split("T")[0]}.csv`,
  };
};

export const fetchApprovePurchaseRequestId = createAsyncThunk(
  "pr/fetchApprovePurchaseRequestId",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_REQUEST?.DETAIL(id)}`
      );
      return response?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch purchase Request by ID"
      );
    }
  }
);

export const fetchApprovePurchaseRequestDropdown = createAsyncThunk(
  "pr/fetchApprovePurchaseRequestDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.APPROVED_PURCHASE_REQUEST?.DROPDOWN}`
      );
      return response?.data?.data || [];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data ||
          "Failed to fetch approve purchase Request Dropdown"
      );
    }
  }
);

export const fetchApprovePurchaseRequestDropdownBySupplierId = createAsyncThunk(
  "pr/fetchApprovePurchaseRequestDropdownBySupplierId",
  async (supplier_id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.APPROVED_PURCHASE_REQUEST?.DROPDOWN_BY_SUPPLIER_ID(
          supplier_id
        )}`
      );
      return response?.data || [];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data ||
          "Failed to fetch approve purchase Request Dropdown"
      );
    }
  }
);
export const fetchApprovePurchaseRequestDropdownBySupplierIdForEdit =
  createAsyncThunk(
    "pr/fetchApprovePurchaseRequestDropdownBySupplierIdForEdit",
    async (
      { supplier_id, po_id }: { supplier_id: string; po_id: string },
      { rejectWithValue }
    ) => {
      try {
        const response = await Axios.get(
          `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.APPROVED_PURCHASE_REQUEST?.DROPDOWN_BY_SUPPLIER_ID_FOR_EDIT(
            supplier_id,
            po_id
          )}`
        );
        return response?.data || [];
      } catch (err: any) {
        return rejectWithValue(
          err.response?.data ||
            "Failed to fetch approve purchase Request Dropdown For Edit"
        );
      }
    }
  );

export const fetchSupplierProductsByPRIDS = createAsyncThunk(
  "pr/fetchSupplierProductsByPRIDS",
  async (
    { pr_ids, supplier_id }: { pr_ids: string[]; supplier_id: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await Axios.post(
        `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.APPROVED_PURCHASE_REQUEST?.PR_PRODUCTS_BY_PR_IDS}`,
        {
          pr_ids,
          supplier_id,
        }
      );
      return response?.data || [];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data ||
          "Failed to fetch approve purchase Request Products"
      );
    }
  }
);

export const revertApprovePr = createAsyncThunk(
  "pr/revertApprovePr",
  async (pr_id: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.APPROVED_PURCHASE_REQUEST?.REVERT_APPROVED_PR(
          pr_id
        )}`
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to Revert Approved PR"
      );
    }
  }
);
